import { LitElement, html, css } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { money as fmtMoney } from '../../format';
import { customElement, state, query, property } from 'lit/decorators.js';
import { api } from '../../api';
import { showToast } from '../../components/ui/toast';
import { store } from '../../store';
import { invalidateCurrencyCache } from '../../components/ui/currency-select';
import { t } from '../../i18n';
import { SETTINGS_SECTIONS } from '../../components/shared/settings-sections';
import { moduleFeatureFlags } from '../../modules/registry';
import type { ThemeMeta } from '../../../shared/themes';
import './backup-panel';

interface Currency { code: string; name: string | null }
interface StripeCfg { enabled: boolean; mode: string; dev_override: boolean; publishable_key: string; has_secret_key: boolean; has_webhook_secret: boolean; has_master_key?: boolean; error?: string; webhook_url: string; }
interface EmailProviderInfo { id: string; label: string; key_prefix: string; hint: string; }
interface EmailCfg { enabled: boolean; dev_override: boolean; sandbox: boolean; provider: string; from_address: string; from_name: string; has_api_key: boolean; has_master_key?: boolean; providers?: EmailProviderInfo[]; error?: string; }

@customElement('cadence-settings-page')
export class CadenceSettingsPage extends LitElement {
  // Which settings section to show. Driven by the URL (/admin/settings/:section) and
  // the nested settings nav in the sidebar — there is no in-page section nav.
  @property() section = 'general';
  @state() private tags: any[] = [];
  @state() private categories: any[] = [];
  @state() private types: any[] = [];
  @state() private levels: any[] = [];
  @state() private newTag = { name: '', color: '#2563eb' };
  @state() private newCat = '';
  @state() private newType: Record<string, string> = {};
  @state() private newLevel = '';
  @state() private eventTypes: any[] = [];
  @state() private newEventType = '';
  @state() private contract: any = null;
  @state() private contractVersions: any[] = [];
  @state() private contractMd = '';
  @state() private publishing = false;
  @state() private viewingVersion: any = null;
  // Billing
  @state() private fees: any[] = [];
  @state() private priceTiers: any[] = [];
  @state() private methods: any[] = [];
  @state() private newMethod = '';
  @state() private newFee: any = { name: '', type: 'monthly', base_amount: 0, currency: '', due_day: 1, scope: 'student', break_behavior: 'bill', break_reduction_percent: '' };
  @state() private newTier: any = { name: '', adjustment_type: 'percent', adjustment_value: 0 };
  @state() private editFeeId = '';
  @state() private editFee: any = {};
  @state() private billing: any = null; // { reminders, late_fee }
  @state() private primaryCurrency = '';
  @state() private currencies: Currency[] = [];
  @state() private newCurrency = '';
  @state() private newCurrencyName = '';
  @state() private stripe: StripeCfg | null = null;
  @state() private stripeForm = { enabled: false, publishable_key: '', secret_key: '', webhook_secret: '' };
  @state() private savingStripe = false;
  @state() private email: EmailCfg | null = null;
  @state() private emailForm = { enabled: false, provider: 'sendgrid', from_address: '', from_name: '', api_key: '' };
  @state() private savingEmail = false;
  // Media upload rules (sizes shown in MB; stored as bytes)
  @state() private mediaForm: { max_photo_mb: number; max_video_mb: number; max_video_seconds: number } | null = null;
  @state() private savingMedia = false;
  // Notifications (configurable admin alerts)
  @state() private notifications: Record<string, boolean> | null = null;
  // Calendar (feature flags + custom items)
  @state() private features: Record<string, boolean> | null = null;
  @state() private myFeed: { enabled: boolean; url: string } | null = null;
  @state() private calItems: any[] = [];
  @state() private newCalItem: any = { title: '', description: '', date: '', start_time: '', end_time: '', is_public: true };
  // Appearance — curated school-wide theme
  @state() private themeCatalog: ThemeMeta[] = [];
  @state() private currentTheme = '';
  @state() private savingTheme = false;
  // General — school profile + break months
  @state() private school: any = null;
  @state() private schoolForm: any = { name: '', contact_email: '', phone: '', address: '', website: '', tagline: '', timezone: 'UTC', default_language: 'en', break_months: [] };
  @state() private savingSchool = false;
  @query('#contract-file') private contractFileInput?: HTMLInputElement;

  private static readonly MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  private get timezones(): string[] {
    try { return (Intl as any).supportedValuesOf?.('timeZone') ?? ['UTC']; } catch { return ['UTC']; }
  }

  static styles = css`
    :host { display: block; }
    h1 { margin: 0 0 var(--space-4); font-size: 1.6rem; }
    /* Long inline code (e.g. the Stripe webhook URL, CLI commands) must wrap, or it
       overflows on narrow screens. */
    code { overflow-wrap: anywhere; }

    /* minmax(0, 1fr) keeps the single column from growing past the viewport when a
       section has wide content (e.g. Billing) — without it the grid blows out and the
       whole page scrolls horizontally on mobile. */
    .panel { padding-top: var(--space-1); display: grid; gap: var(--space-4); grid-template-columns: minmax(0, 1fr); }
    .row { display: flex; gap: 10px; align-items: flex-end; flex-wrap: wrap; }
    .list { display: flex; flex-direction: column; gap: 8px; }
    .item { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-primary); }
    .left { display: flex; align-items: center; gap: 10px; }
    .dot { width: 14px; height: 14px; border-radius: 50%; }
    .rm { cursor: pointer; color: var(--color-danger); background: none; border: none; font-size: 0.85rem; }
    .actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
    .act { cursor: pointer; background: none; border: 1px solid var(--color-border); border-radius: var(--radius-sm); padding: 4px 10px; font: inherit; font-size: 0.82rem; color: var(--color-text-primary); }
    .act:hover { border-color: var(--color-primary); color: var(--color-primary); }
    .item.editing { flex-direction: column; align-items: stretch; gap: 12px; }
    .edit-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px; }
    .item.editing .actions { justify-content: flex-end; }
    .cat { font-weight: 700; margin: var(--space-3) 0 6px; }
    .nested { padding-left: var(--space-4); }
    input[type=color] { width: 42px; height: 38px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: none; }
    .versions { display: flex; flex-direction: column; gap: 6px; }
    .ver { display: flex; align-items: center; gap: 14px; padding: 6px 8px 6px 12px; border: 1px solid var(--color-border); border-radius: var(--radius-md); font-size: 0.88rem; }
    .ver .v { font-weight: 700; }
    .ver-count { margin-left: auto; }
    .ver-doc { max-height: 60vh; overflow-y: auto; line-height: 1.5; }
    .ver-doc :first-child { margin-top: 0; }
    .ver-doc h1, .ver-doc h2, .ver-doc h3 { margin: 1em 0 0.4em; }
    .ver-doc ul, .ver-doc ol { padding-left: 1.4em; }
    .muted { color: var(--color-text-secondary); font-size: 0.85rem; }
    .form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
    .form-grid .full { grid-column: 1 / -1; }
    @media (max-width: 640px) { .form-grid { grid-template-columns: 1fr; } }
    .logo-row { display: flex; align-items: center; gap: 18px; }
    .logo-meta { display: flex; flex-direction: column; gap: 4px; }
    .field-label { font-weight: 600; font-size: 0.9rem; }
    .save-row { display: flex; justify-content: flex-end; gap: 10px; }
    .months { display: grid; grid-template-columns: repeat(6, 1fr); gap: 8px; max-width: 520px; }
    @media (max-width: 560px) { .months { grid-template-columns: repeat(4, 1fr); } }
    .chip { font: inherit; font-size: 0.85rem; padding: 8px 0; border-radius: var(--radius-md); border: 1px solid var(--color-border); background: var(--color-bg-primary); color: var(--color-text-primary); cursor: pointer; }
    .chip.on { background: var(--color-primary); color: #fff; border-color: var(--color-primary); }
    .theme-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 14px; }
    .theme-card {
      text-align: left; cursor: pointer; padding: 0; overflow: hidden; font: inherit;
      border: 1px solid var(--color-border); border-radius: var(--radius-lg);
      background: var(--color-bg-primary); color: var(--color-text-primary);
      transition: border-color 0.15s, box-shadow 0.15s, transform 0.1s;
    }
    .theme-card:hover { border-color: var(--color-primary); transform: translateY(-2px); box-shadow: var(--shadow-md); }
    .theme-card[aria-pressed='true'] { border-color: var(--color-primary); box-shadow: 0 0 0 2px var(--color-primary); }
    .theme-card:disabled { cursor: default; opacity: 0.7; }
    .theme-swatch { display: flex; height: 56px; }
    .theme-swatch span { flex: 1; }
    .theme-foot { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 10px 12px; }
    .theme-name { font-weight: 600; font-size: 0.92rem; }
    .theme-current { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--color-primary); }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }

  private async load() {
    const [tags, categories, types, levels] = await Promise.all([
      api.get<any[]>('/tags'), api.get<any[]>('/dance/categories'), api.get<any[]>('/dance/types'), api.get<any[]>('/dance/levels'),
    ]);
    this.tags = tags; this.categories = categories; this.types = types; this.levels = levels;
    await Promise.all([this.loadContract(), this.loadBilling(), this.loadEventTypes(), this.loadMedia(), this.loadCalendar(), this.loadNotifications(), this.loadSchool(), this.loadThemes()]);
  }

  private async loadThemes() {
    try {
      const r = await api.get<{ themes: ThemeMeta[]; current: string }>('/settings/themes');
      this.themeCatalog = r.themes; this.currentTheme = r.current;
    } catch { /* lacks settings.view */ }
  }

  private async selectTheme(id: string) {
    if (id === this.currentTheme || this.savingTheme) return;
    const prev = this.currentTheme;
    this.currentTheme = id;
    this.savingTheme = true;
    store.applyThemeId(id); // instant live preview for this admin
    try {
      await api.put('/settings/theme', { theme_id: id });
      showToast(t('settings.appearance.saved'), 'success');
    } catch (e) {
      this.currentTheme = prev;
      store.applyThemeId(prev || 'ocean');
      showToast((e as Error).message, 'error');
    } finally { this.savingTheme = false; }
  }

  private async loadSchool() {
    try {
      const s = await api.get<any>('/settings/school');
      this.school = s;
      this.schoolForm = {
        name: s.name ?? '', contact_email: s.contact_email ?? '', phone: s.phone ?? '',
        address: s.address ?? '', website: s.website ?? '', tagline: s.tagline ?? '', timezone: s.timezone ?? 'UTC',
        default_language: s.default_language ?? 'en', break_months: [...(s.break_months ?? [])],
      };
    } catch { /* lacks settings.view */ }
  }

  private async saveSchool() {
    if (!this.schoolForm.name.trim()) { showToast(t('settings.toast.schoolNameRequired'), 'error'); return; }
    this.savingSchool = true;
    try {
      this.school = await api.put<any>('/settings/school', this.schoolForm);
      store.set({ school: { name: this.school.name, logo_url: this.school.logo_url } });
      showToast(t('settings.toast.schoolSaved'), 'success');
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.savingSchool = false; }
  }

  private toggleBreakMonth(m: number) {
    const set = new Set<number>(this.schoolForm.break_months);
    if (set.has(m)) set.delete(m); else set.add(m);
    this.schoolForm = { ...this.schoolForm, break_months: [...set].sort((a, b) => a - b) };
  }

  private onLogoUploaded(url: string | null) {
    if (this.school) this.school = { ...this.school, logo_url: url };
    store.set({ school: { name: this.school?.name ?? store.get().school?.name ?? '', logo_url: url } });
  }

  private async clearLogo() {
    try { await api.del('/settings/logo'); this.onLogoUploaded(null); showToast(t('settings.toast.logoRemoved'), 'success'); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }

  private async loadCalendar() {
    try {
      const [features, items, me] = await Promise.all([
        api.get<Record<string, boolean>>('/settings/features'),
        api.get<any[]>('/calendar/items'),
        api.get<{ enabled: boolean; url: string }>('/calendar/me').catch(() => null),
      ]);
      this.features = features; this.calItems = items; this.myFeed = me;
    } catch { /* lacks settings.view */ }
  }

  private async loadNotifications() {
    try { this.notifications = await api.get<Record<string, boolean>>('/settings/notifications'); }
    catch { /* lacks settings.view */ }
  }

  private async toggleNotification(key: string, value: boolean) {
    const prev = this.notifications;
    this.notifications = { ...(prev ?? {}), [key]: value };
    try {
      this.notifications = await api.put<Record<string, boolean>>('/settings/notifications', { [key]: value });
      showToast(t('settings.toast.settingsSaved'), 'success');
    } catch (e) { this.notifications = prev; showToast((e as Error).message, 'error'); }
  }

  private async toggleFeature(flag: string, value: boolean) {
    const prev = this.features;
    this.features = { ...(prev ?? {}), [flag]: value };
    try {
      this.features = await api.put<Record<string, boolean>>('/settings/features', { [flag]: value });
      // Reflect the change in the global store so the sidebar/portal nav and page
      // guards update immediately, without needing a reload.
      store.set({ features: { ...store.get().features, ...this.features } });
      showToast(t('settings.toast.settingsSaved'), 'success');
    } catch (e) { this.features = prev; showToast((e as Error).message, 'error'); }
  }

  private async copyFeed() {
    if (!this.myFeed?.url) return;
    try { await navigator.clipboard.writeText(this.myFeed.url); showToast(t('settings.toast.feedCopied'), 'success'); }
    catch { showToast(t('settings.toast.copyFailed'), 'error'); }
  }

  private async addCalItem() {
    const it = this.newCalItem;
    if (!it.title.trim() || !it.date) { showToast(t('settings.toast.titleDateRequired'), 'error'); return; }
    try {
      await api.post('/calendar/items', {
        title: it.title.trim(), description: it.description || null, date: it.date,
        start_time: it.start_time || null, end_time: it.end_time || null, is_public: it.is_public !== false,
      });
      this.newCalItem = { title: '', description: '', date: '', start_time: '', end_time: '', is_public: true };
      showToast(t('settings.toast.calItemAdded'), 'success');
      await this.loadCalendar();
    } catch (e) { showToast((e as Error).message, 'error'); }
  }

  private async delCalItem(id: string) {
    try {
      await api.del(`/calendar/items/${id}`);
      await this.loadCalendar();
    } catch (e) { showToast((e as Error).message, 'error'); }
  }

  private renderCalendar() {
    return html`<div class="panel">
      <cadence-card heading=${t('settings.calendar.featureHeading')}>
        <div class="list">
          <cadence-toggle label=${t('settings.calendar.enableFeeds')} ?checked=${this.features?.feature_calendar !== false}
            @change=${(e: CustomEvent) => this.toggleFeature('feature_calendar', e.detail)}></cadence-toggle>
          <p class="muted">${t('settings.calendar.enableFeedsHint')}</p>
          ${this.myFeed?.url ? html`
            <div class="row" style="align-items:center">
              <span class="muted">${t('settings.calendar.yourFeed')}</span>
              <code style="font-size:0.8rem;word-break:break-all">${this.myFeed.url}</code>
              <button class="act" @click=${() => this.copyFeed()}>${t('settings.actions.copy')}</button>
            </div>` : ''}
        </div>
      </cadence-card>

      <cadence-card heading=${t('settings.calendar.itemsHeading')}>
        <p class="muted">${t('settings.calendar.itemsHint')}</p>
        <div class="edit-grid">
          <cadence-input label=${t('settings.calendar.title')} .value=${this.newCalItem.title}
            @input=${(e: CustomEvent) => (this.newCalItem = { ...this.newCalItem, title: e.detail })}></cadence-input>
          <cadence-input label=${t('settings.calendar.date')} type="date" .value=${this.newCalItem.date}
            @input=${(e: CustomEvent) => (this.newCalItem = { ...this.newCalItem, date: e.detail })}></cadence-input>
          <cadence-input label=${t('settings.calendar.startTime')} type="time" .value=${this.newCalItem.start_time}
            @input=${(e: CustomEvent) => (this.newCalItem = { ...this.newCalItem, start_time: e.detail })}></cadence-input>
          <cadence-input label=${t('settings.calendar.endTime')} type="time" .value=${this.newCalItem.end_time}
            @input=${(e: CustomEvent) => (this.newCalItem = { ...this.newCalItem, end_time: e.detail })}></cadence-input>
          <cadence-input label=${t('settings.calendar.description')} .value=${this.newCalItem.description}
            @input=${(e: CustomEvent) => (this.newCalItem = { ...this.newCalItem, description: e.detail })}></cadence-input>
        </div>
        <div class="row" style="margin-top:12px">
          <cadence-button @click=${() => this.addCalItem()}>${t('settings.calendar.addItem')}</cadence-button>
        </div>
        <div class="list" style="margin-top:16px">
          ${this.calItems.map((it) => html`
            <div class="item">
              <div class="left">
                <span><strong>${it.title}</strong> — ${it.date}${it.start_time ? ` ${it.start_time}` : ''}</span>
                ${it.is_public ? '' : html`<cadence-badge>${t('settings.calendar.hidden')}</cadence-badge>`}
              </div>
              <button class="rm" @click=${() => this.delCalItem(it.id)}>${t('settings.actions.delete')}</button>
            </div>`)}
          ${this.calItems.length ? '' : html`<cadence-empty-state heading=${t('settings.calendar.emptyItems')}></cadence-empty-state>`}
        </div>
      </cadence-card>
    </div>`;
  }

  private async loadMedia() {
    try {
      const m = await api.get<{ max_photo_bytes: number; max_video_bytes: number; max_video_seconds: number }>('/settings/media');
      this.mediaForm = {
        max_photo_mb: Math.round(m.max_photo_bytes / 1024 / 1024),
        max_video_mb: Math.round(m.max_video_bytes / 1024 / 1024),
        max_video_seconds: m.max_video_seconds,
      };
    } catch { /* lacks settings.view */ }
  }

  private async saveMedia() {
    if (!this.mediaForm) return;
    this.savingMedia = true;
    try {
      await api.put('/settings/media', {
        max_photo_bytes: Math.max(1, Number(this.mediaForm.max_photo_mb) || 0) * 1024 * 1024,
        max_video_bytes: Math.max(1, Number(this.mediaForm.max_video_mb) || 0) * 1024 * 1024,
        max_video_seconds: Math.max(1, Number(this.mediaForm.max_video_seconds) || 0),
      });
      showToast(t('settings.toast.mediaSaved'), 'success');
      await this.loadMedia();
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.savingMedia = false; }
  }

  private renderMedia() {
    const m = this.mediaForm;
    if (!m) return html`<div class="panel"><cadence-empty-state heading=${t('settings.media.noAccessHeading')} message=${t('settings.media.noAccess')}></cadence-empty-state></div>`;
    return html`<div class="panel">
      <cadence-card heading=${t('settings.media.uploadRules')}>
        <p class="muted" style="margin-top:0">${t('settings.media.uploadRulesHint')}</p>
        <div class="row">
          <cadence-input label=${t('settings.media.maxPhoto')} type="number" .value=${String(m.max_photo_mb)}
            @input=${(e: CustomEvent) => (this.mediaForm = { ...m, max_photo_mb: e.detail })}></cadence-input>
          <cadence-input label=${t('settings.media.maxVideo')} type="number" .value=${String(m.max_video_mb)}
            @input=${(e: CustomEvent) => (this.mediaForm = { ...m, max_video_mb: e.detail })}></cadence-input>
          <cadence-input label=${t('settings.media.maxVideoDuration')} type="number" .value=${String(m.max_video_seconds)}
            @input=${(e: CustomEvent) => (this.mediaForm = { ...m, max_video_seconds: e.detail })}></cadence-input>
        </div>
        <div style="margin-top:14px">
          <cadence-button ?loading=${this.savingMedia} @click=${() => this.saveMedia()}>${t('settings.media.save')}</cadence-button>
        </div>
      </cadence-card>
    </div>`;
  }

  private async loadEventTypes() {
    try { this.eventTypes = await api.get<any[]>('/events/types'); }
    catch { /* lacks events.view — Event Types tab simply stays empty */ }
  }

  private async loadBilling() {
    try {
      const [fees, tiers, methods] = await Promise.all([
        api.get<any[]>('/fees?all=1'), api.get<any[]>('/fees/tiers'), api.get<any[]>('/fees/methods'),
      ]);
      this.fees = fees; this.priceTiers = tiers; this.methods = methods;
    } catch { /* lacks payments.view — billing tab simply stays empty */ }
    try { this.billing = await api.get<any>('/settings/billing'); } catch { /* lacks settings.view */ }
    try { this.primaryCurrency = (await api.get<{ primary_currency: string }>('/settings/currency')).primary_currency; } catch { /* lacks settings.view */ }
    try { this.currencies = await api.get<Currency[]>('/currencies'); } catch { /* lacks auth */ }
    try {
      this.stripe = await api.get<StripeCfg>('/settings/stripe');
      this.stripeForm = { enabled: this.stripe.enabled, publishable_key: this.stripe.publishable_key, secret_key: '', webhook_secret: '' };
    } catch { /* lacks settings.view */ }
    try {
      this.email = await api.get<EmailCfg>('/settings/email');
      this.emailForm = { enabled: this.email.enabled, provider: this.email.provider || 'sendgrid', from_address: this.email.from_address, from_name: this.email.from_name, api_key: '' };
    } catch { /* lacks settings.view */ }
  }

  private async saveEmail() {
    this.savingEmail = true;
    try {
      const payload: any = { enabled: this.emailForm.enabled, provider: this.emailForm.provider, from_address: this.emailForm.from_address.trim(), from_name: this.emailForm.from_name.trim() };
      if (this.emailForm.api_key.trim()) payload.api_key = this.emailForm.api_key.trim();
      await api.put('/settings/email', payload);
      showToast(t('settings.toast.emailSaved'), 'success');
      await this.loadBilling();
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.savingEmail = false; }
  }

  private renderEmail() {
    const e = this.email;
    if (!e) return html`<div class="panel"><cadence-empty-state heading=${t('settings.email.noAccessHeading')} message=${t('settings.email.noAccess')}></cadence-empty-state></div>`;
    const blocked = !e.dev_override && e.has_master_key === false;
    const providers = e.providers?.length ? e.providers : [{ id: 'sendgrid', label: 'SendGrid', key_prefix: 'SG.', hint: '' }];
    const meta = providers.find((p) => p.id === this.emailForm.provider) ?? providers[0];
    return html`<div class="panel">
      <cadence-card heading=${t('settings.email.providerHeading')}>
        ${e.dev_override ? html`<p class="muted" style="margin-top:0;padding:10px 12px;border:1px solid var(--color-border);border-radius:var(--radius-sm)">🧪 ${t('settings.email.devOverridePre', { provider: meta.label })} <code>.dev.vars</code>${t('settings.email.devOverridePost')}</p>` : ''}
        ${blocked ? html`<p class="muted" style="color:var(--color-danger)">⚠️ ${t('settings.email.blockedPre')} <code>ENCRYPTION_KEY</code> ${t('settings.email.blockedPost')}</p>` : ''}
        ${e.error ? html`<p class="muted" style="color:var(--color-danger)">⚠️ ${e.error}</p>` : ''}
        <p class="muted">${t('settings.status.label')} <strong>${e.enabled ? t('settings.status.enabled') : t('settings.status.disabled')}</strong>${e.sandbox ? t('settings.email.sandboxSuffix') : ''}. ${t('settings.email.statusUse')}</p>
        <div class="row" style="margin-bottom:12px">
          <cadence-toggle label=${t('settings.email.enableSending')} ?checked=${this.emailForm.enabled}
            @change=${(ev: CustomEvent) => (this.emailForm = { ...this.emailForm, enabled: ev.detail })}></cadence-toggle>
        </div>
        <div style="display:flex;flex-direction:column;gap:12px;max-width:520px">
          ${e.dev_override ? '' : html`<cadence-select label=${t('settings.email.provider')}
            .options=${providers.map((p) => ({ value: p.id, label: p.label }))} .value=${this.emailForm.provider}
            @change=${(ev: CustomEvent) => (this.emailForm = { ...this.emailForm, provider: ev.detail, api_key: '' })}></cadence-select>`}
          <cadence-input label=${t('settings.email.fromAddress')} placeholder=${t('settings.email.fromAddressPlaceholder')}
            .value=${this.emailForm.from_address} @input=${(ev: CustomEvent) => (this.emailForm = { ...this.emailForm, from_address: ev.detail })}></cadence-input>
          <cadence-input label=${t('settings.email.fromName')} placeholder=${store.get().school?.name || t('settings.email.fromNamePlaceholder')}
            .value=${this.emailForm.from_name} @input=${(ev: CustomEvent) => (this.emailForm = { ...this.emailForm, from_name: ev.detail })}></cadence-input>
          <p class="muted" style="margin:-4px 0 0">${t('settings.email.fromNameHint')}</p>
          ${e.dev_override ? '' : html`<cadence-input label=${t('settings.email.apiKey', { provider: meta.label })} type="password"
            placeholder=${e.has_api_key && e.provider === this.emailForm.provider ? t('settings.billing.savedPlaceholder') : `${meta.key_prefix}…`}
            .value=${this.emailForm.api_key} @input=${(ev: CustomEvent) => (this.emailForm = { ...this.emailForm, api_key: ev.detail })}></cadence-input>`}
        </div>
        ${e.dev_override ? '' : html`<p class="muted" style="margin-top:10px">${meta.hint}</p>`}
        <div style="margin-top:14px">
          <cadence-button ?loading=${this.savingEmail} ?disabled=${blocked} @click=${() => this.saveEmail()}>${t('settings.email.save')}</cadence-button>
        </div>
      </cadence-card>
    </div>`;
  }

  private async savePrimaryCurrency() {
    const cur = (this.primaryCurrency || '').trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(cur)) { showToast(t('settings.toast.currencyCode3Ron'), 'error'); return; }
    try {
      this.primaryCurrency = (await api.put<{ primary_currency: string }>('/settings/currency', { primary_currency: cur })).primary_currency;
      // Adding/changing the primary auto-includes it in the managed list (server seed),
      // so refresh the list + invalidate the shared picker cache.
      if (!this.currencies.some((x) => x.code === cur)) { try { this.currencies = await api.get<Currency[]>('/currencies'); invalidateCurrencyCache(); } catch { /* ignore */ } }
      showToast(t('settings.toast.primaryCurrencySaved'), 'success');
    } catch (e) { showToast((e as Error).message, 'error'); }
  }

  private async addCurrency() {
    const code = (this.newCurrency || '').trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(code)) { showToast(t('settings.toast.currencyCode3Eur'), 'error'); return; }
    try {
      this.currencies = await api.post<Currency[]>('/currencies', { code, name: this.newCurrency && this.newCurrencyName.trim() ? this.newCurrencyName.trim() : null });
      this.newCurrency = ''; this.newCurrencyName = '';
      invalidateCurrencyCache();
      showToast(t('settings.toast.currencyAdded'), 'success');
    } catch (e) { showToast((e as Error).message, 'error'); }
  }

  private async removeCurrency(code: string) {
    try {
      this.currencies = await api.del<Currency[]>(`/currencies/${code}`);
      invalidateCurrencyCache();
      showToast(t('settings.toast.currencyRemoved'), 'success');
    } catch (e) { showToast((e as Error).message, 'error'); }
  }

  private async saveBilling(patch: any) {
    try {
      this.billing = await api.put<any>('/settings/billing', patch);
      showToast(t('settings.toast.billingSaved'), 'success');
    } catch (e) { showToast((e as Error).message, 'error'); }
  }

  private async saveStripe() {
    this.savingStripe = true;
    try {
      // Only send secret/webhook when the admin typed a new value (blank keeps the stored one).
      const payload: any = { enabled: this.stripeForm.enabled, publishable_key: this.stripeForm.publishable_key.trim() };
      if (this.stripeForm.secret_key.trim()) payload.secret_key = this.stripeForm.secret_key.trim();
      if (this.stripeForm.webhook_secret.trim()) payload.webhook_secret = this.stripeForm.webhook_secret.trim();
      await api.put('/settings/stripe', payload);
      showToast(t('settings.toast.stripeSaved'), 'success');
      await this.loadBilling();
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.savingStripe = false; }
  }

  private renderStripe() {
    const s = this.stripe!;
    const modeLabel = s.mode === 'test' ? t('settings.billing.stripeMode.test') : s.mode === 'live' ? t('settings.billing.stripeMode.live') : t('settings.billing.stripeMode.none');
    return html`
      <cadence-card heading=${t('settings.billing.stripeHeading')}>
        ${s.dev_override ? html`
          <p class="muted" style="margin-top:0;padding:10px 12px;border:1px solid var(--color-border);border-radius:var(--radius-sm)">
            🧪 ${t('settings.billing.stripeDevPre')} <strong>${t('settings.billing.stripeDevStrong')} <code>.dev.vars</code></strong>${t('settings.billing.stripeDevPost')}
          </p>` : ''}
        ${!s.dev_override && s.has_master_key === false ? html`
          <p style="margin-top:0;padding:10px 12px;border:1px solid var(--color-danger);border-radius:var(--radius-sm);color:var(--color-danger)">
            ⚠️ ${t('settings.billing.stripeNoKeyPre')} <code>wrangler secret put ENCRYPTION_KEY</code> ${t('settings.billing.stripeNoKeyPost')}
          </p>` : ''}
        ${!s.dev_override && s.error ? html`
          <p style="margin-top:0;padding:10px 12px;border:1px solid var(--color-danger);border-radius:var(--radius-sm);color:var(--color-danger)">
            ⚠️ ${t('settings.billing.stripeErrorPre')} ${s.error}${t('settings.billing.stripeErrorMid')} <code>ENCRYPTION_KEY</code> ${t('settings.billing.stripeErrorPost')}
          </p>` : ''}
        <p class="muted" style="margin-top:0">
          ${t('settings.status.label')} <strong>${s.enabled ? t('settings.status.enabled') : t('settings.status.disabled')}</strong> · ${modeLabel}.
          ${t('settings.billing.stripeStatusUse')}
        </p>

        <div class="row" style="margin-bottom:12px">
          <cadence-toggle label=${t('settings.billing.stripeEnable')} ?checked=${this.stripeForm.enabled}
            @change=${(e: CustomEvent) => (this.stripeForm = { ...this.stripeForm, enabled: e.detail })}></cadence-toggle>
        </div>
        <div style="display:flex;flex-direction:column;gap:12px;max-width:520px">
          <cadence-input label=${t('settings.billing.stripePublishable')} placeholder="pk_live_…"
            .value=${this.stripeForm.publishable_key}
            @input=${(e: CustomEvent) => (this.stripeForm = { ...this.stripeForm, publishable_key: e.detail })}></cadence-input>
          <cadence-input label=${t('settings.billing.stripeSecret')} type="password"
            placeholder=${s.has_secret_key ? t('settings.billing.savedPlaceholder') : 'sk_live_… or rk_live_…'}
            .value=${this.stripeForm.secret_key}
            @input=${(e: CustomEvent) => (this.stripeForm = { ...this.stripeForm, secret_key: e.detail })}></cadence-input>
          <cadence-input label=${t('settings.billing.stripeWebhook')} type="password"
            placeholder=${s.has_webhook_secret ? t('settings.billing.savedPlaceholder') : 'whsec_…'}
            .value=${this.stripeForm.webhook_secret}
            @input=${(e: CustomEvent) => (this.stripeForm = { ...this.stripeForm, webhook_secret: e.detail })}></cadence-input>
        </div>

        <p class="muted" style="margin-top:12px">
          ${t('settings.billing.stripeWebhookHelp1')}
          <code>checkout.session.completed</code> ${t('settings.billing.stripeWebhookHelp2')}<br />
          <code>${s.webhook_url}</code><br />
          ${t('settings.billing.stripeWebhookHelp3')}<code>rk_…</code>${t('settings.billing.stripeWebhookHelp4')}
        </p>

        <div style="margin-top:14px">
          <cadence-button ?loading=${this.savingStripe} ?disabled=${!s.dev_override && s.has_master_key === false} @click=${() => this.saveStripe()}>${s.dev_override ? t('settings.billing.stripeSaveDev') : t('settings.billing.stripeSave')}</cadence-button>
        </div>
      </cadence-card>`;
  }

  private async createFee() {
    const f = this.newFee;
    if (!f.name || !(f.base_amount >= 0) || !f.currency) { showToast(t('settings.toast.feeFieldsRequired'), 'error'); return; }
    try {
      await api.post('/fees', {
        ...f, base_amount: Number(f.base_amount), due_day: Number(f.due_day) || null,
        break_reduction_percent: f.break_behavior === 'reduce' ? Number(f.break_reduction_percent) || 0 : null,
      });
      this.newFee = { name: '', type: 'monthly', base_amount: 0, currency: f.currency, due_day: 1, scope: f.scope, break_behavior: 'bill', break_reduction_percent: '' };
      await this.loadBilling(); showToast(t('settings.toast.feeCreated'), 'success');
    } catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async createTier() {
    const tier = this.newTier;
    if (!tier.name) { showToast(t('settings.toast.tierNameRequired'), 'error'); return; }
    try {
      await api.post('/fees/tiers', { ...tier, adjustment_value: Number(tier.adjustment_value) });
      this.newTier = { name: '', adjustment_type: 'percent', adjustment_value: 0 };
      await this.loadBilling(); showToast(t('settings.toast.tierCreated'), 'success');
    } catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async delTier(id: string) { try { await api.del(`/fees/tiers/${id}`); await this.loadBilling(); } catch (e) { showToast((e as Error).message, 'error'); } }

  private async addMethod() {
    const name = this.newMethod.trim();
    if (!name) { showToast(t('settings.toast.methodNameRequired'), 'error'); return; }
    try {
      await api.post('/fees/methods', { name });
      this.newMethod = '';
      await this.loadBilling(); showToast(t('settings.toast.methodAdded'), 'success');
    } catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async setDefaultMethod(id: string) {
    try { await api.post(`/fees/methods/${id}/default`, {}); await this.loadBilling(); showToast(t('settings.toast.defaultMethodSet'), 'success'); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async toggleMethod(m: any) {
    try { await api.put(`/fees/methods/${m.id}`, { enabled: !m.enabled }); await this.loadBilling(); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async deleteMethod(id: string) {
    try { await api.del(`/fees/methods/${id}`); await this.loadBilling(); showToast(t('settings.toast.methodDeleted'), 'success'); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }

  private startEditFee(f: any) {
    this.editFeeId = f.id;
    this.editFee = {
      name: f.name, type: f.type, base_amount: f.base_amount, currency: f.currency, due_day: f.due_day ?? '',
      break_behavior: f.break_behavior ?? 'bill', break_reduction_percent: f.break_reduction_percent ?? '',
    };
  }
  private cancelEditFee() { this.editFeeId = ''; this.editFee = {}; }
  private async saveEditFee() {
    const f = this.editFee;
    if (!f.name || !f.currency) { showToast(t('settings.toast.feeNameCurrencyRequired'), 'error'); return; }
    try {
      await api.put(`/fees/${this.editFeeId}`, {
        name: f.name, type: f.type, base_amount: Number(f.base_amount), currency: f.currency, due_day: Number(f.due_day) || null,
        break_behavior: f.break_behavior ?? 'bill',
        break_reduction_percent: f.break_behavior === 'reduce' ? Number(f.break_reduction_percent) || 0 : null,
      });
      this.cancelEditFee(); await this.loadBilling(); showToast(t('settings.toast.feeUpdated'), 'success');
    } catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async archiveFee(f: any) {
    try { await api.post(`/fees/${f.id}/archive`, { active: !f.active }); await this.loadBilling(); showToast(f.active ? t('settings.toast.feeArchived') : t('settings.toast.feeRestored'), 'success'); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async deleteFee(f: any) {
    try { await api.del(`/fees/${f.id}`); await this.loadBilling(); showToast(t('settings.toast.feeDeleted'), 'success'); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }

  private renderBilling() {
    const money = (a: number, c: string) => fmtMoney(a, c);
    return html`<div class="panel">
      <cadence-card heading=${t('settings.billing.primaryCurrencyHeading')}>
        <p class="muted" style="margin-top:0">${t('settings.billing.primaryCurrencyHint')}</p>
        <div class="row">
          <cadence-currency-select label=${t('settings.billing.currencyCode')} placeholder=${t('settings.billing.currencyCodePlaceholder')} .value=${this.primaryCurrency}
            @change=${(e: CustomEvent) => (this.primaryCurrency = e.detail)}></cadence-currency-select>
          <cadence-button @click=${() => this.savePrimaryCurrency()}>${t('common.actions.save')}</cadence-button>
        </div>
        <p class="muted" style="margin-bottom:0">${t('settings.billing.primaryCurrencyNote')}</p>
      </cadence-card>

      <cadence-card heading=${t('settings.billing.currenciesHeading')}>
        <p class="muted" style="margin-top:0">${t('settings.billing.currenciesHint')}</p>
        <div class="row" style="margin-bottom:12px">
          <cadence-input label=${t('settings.billing.code')} placeholder=${t('settings.billing.codePlaceholder')} .value=${this.newCurrency}
            @input=${(e: CustomEvent) => (this.newCurrency = e.detail)}></cadence-input>
          <cadence-input label=${t('settings.billing.nameOptional')} placeholder=${t('settings.billing.namePlaceholder')} .value=${this.newCurrencyName}
            @input=${(e: CustomEvent) => (this.newCurrencyName = e.detail)}></cadence-input>
          <cadence-button @click=${() => this.addCurrency()}>${t('settings.actions.add')}</cadence-button>
        </div>
        ${this.currencies.length === 0
          ? html`<p class="muted">${t('settings.billing.noCurrencies')}</p>`
          : this.currencies.map((cur) => {
              const isPrimary = cur.code === this.primaryCurrency;
              return html`<div class="item">
                <div class="left"><strong>${cur.code}</strong>${cur.name ? html` <span class="muted">${cur.name}</span>` : ''}${isPrimary ? html` <span class="muted">${t('settings.billing.primarySuffix')}</span>` : ''}</div>
                <div class="actions">
                  ${isPrimary
                    ? html`<span class="muted">${t('settings.billing.inUse')}</span>`
                    : html`<cadence-button variant="danger" @click=${() => this.removeCurrency(cur.code)}>${t('settings.actions.remove')}</cadence-button>`}
                </div>
              </div>`;
            })}
      </cadence-card>

      <cadence-card heading=${t('settings.billing.feeDefinitionsHeading')}>
        <div class="row" style="margin-bottom:12px">
          <cadence-input label=${t('settings.billing.feeName')} placeholder=${t('settings.billing.feeNamePlaceholder')} .value=${this.newFee.name} @input=${(e: CustomEvent) => (this.newFee = { ...this.newFee, name: e.detail })}></cadence-input>
          <cadence-select label=${t('settings.billing.feeType')} .value=${this.newFee.type}
            .options=${[{ value: 'monthly', label: t('settings.billing.feeTypes.monthly') }, { value: 'yearly', label: t('settings.billing.feeTypes.yearly') }, { value: 'occasional', label: t('settings.billing.feeTypes.occasional') }, { value: 'event', label: t('settings.billing.feeTypes.event') }]}
            @change=${(e: CustomEvent) => (this.newFee = { ...this.newFee, type: e.detail })}></cadence-select>
          <cadence-select label=${t('settings.billing.billed')} .value=${this.newFee.scope}
            .options=${[{ value: 'student', label: t('settings.billing.scope.student') }, { value: 'household', label: t('settings.billing.scope.household') }]}
            @change=${(e: CustomEvent) => (this.newFee = { ...this.newFee, scope: e.detail })}></cadence-select>
          <cadence-input label=${this.newFee.scope === 'household' ? t('settings.billing.defaultAmount') : t('settings.billing.amount')} type="number" .value=${String(this.newFee.base_amount)} @input=${(e: CustomEvent) => (this.newFee = { ...this.newFee, base_amount: e.detail })}></cadence-input>
          <cadence-currency-select placeholder=${t('settings.billing.selectPlaceholder')} .value=${this.newFee.currency} @change=${(e: CustomEvent) => (this.newFee = { ...this.newFee, currency: e.detail })}></cadence-currency-select>
          <cadence-input label=${t('settings.billing.dueDay')} type="number" .value=${String(this.newFee.due_day)} @input=${(e: CustomEvent) => (this.newFee = { ...this.newFee, due_day: e.detail })}></cadence-input>
          ${this.newFee.type === 'monthly' ? html`
            <cadence-select label=${t('settings.billing.breakBehavior')} .value=${this.newFee.break_behavior}
              .options=${[{ value: 'bill', label: t('settings.billing.breakBehaviors.bill') }, { value: 'pause', label: t('settings.billing.breakBehaviors.pause') }, { value: 'reduce', label: t('settings.billing.breakBehaviors.reduce') }]}
              @change=${(e: CustomEvent) => (this.newFee = { ...this.newFee, break_behavior: e.detail })}></cadence-select>
            ${this.newFee.break_behavior === 'reduce' ? html`<cadence-input label=${t('settings.billing.reduceBy')} type="number" .value=${String(this.newFee.break_reduction_percent)} @input=${(e: CustomEvent) => (this.newFee = { ...this.newFee, break_reduction_percent: e.detail })}></cadence-input>` : ''}
          ` : ''}
          <cadence-button @click=${() => this.createFee()}>${t('settings.billing.addFee')}</cadence-button>
        </div>
        <div class="list">
          ${this.fees.map((f) => this.editFeeId === f.id
            ? html`<div class="item editing">
                <div class="edit-grid">
                  <cadence-input label=${t('settings.billing.feeName')} .value=${this.editFee.name} @input=${(e: CustomEvent) => (this.editFee = { ...this.editFee, name: e.detail })}></cadence-input>
                  <cadence-select label=${t('settings.billing.feeType')} .value=${this.editFee.type}
                    .options=${[{ value: 'monthly', label: t('settings.billing.feeTypes.monthly') }, { value: 'yearly', label: t('settings.billing.feeTypes.yearly') }, { value: 'occasional', label: t('settings.billing.feeTypes.occasional') }, { value: 'event', label: t('settings.billing.feeTypes.event') }]}
                    @change=${(e: CustomEvent) => (this.editFee = { ...this.editFee, type: e.detail })}></cadence-select>
                  <cadence-input label=${t('settings.billing.amount')} type="number" .value=${String(this.editFee.base_amount)} @input=${(e: CustomEvent) => (this.editFee = { ...this.editFee, base_amount: e.detail })}></cadence-input>
                  <cadence-currency-select .value=${this.editFee.currency} @change=${(e: CustomEvent) => (this.editFee = { ...this.editFee, currency: e.detail })}></cadence-currency-select>
                  <cadence-input label=${t('settings.billing.dueDay')} type="number" .value=${String(this.editFee.due_day)} @input=${(e: CustomEvent) => (this.editFee = { ...this.editFee, due_day: e.detail })}></cadence-input>
                  ${this.editFee.type === 'monthly' ? html`
                    <cadence-select label=${t('settings.billing.breakBehavior')} .value=${this.editFee.break_behavior}
                      .options=${[{ value: 'bill', label: t('settings.billing.breakBehaviors.bill') }, { value: 'pause', label: t('settings.billing.breakBehaviors.pause') }, { value: 'reduce', label: t('settings.billing.breakBehaviors.reduce') }]}
                      @change=${(e: CustomEvent) => (this.editFee = { ...this.editFee, break_behavior: e.detail })}></cadence-select>
                    ${this.editFee.break_behavior === 'reduce' ? html`<cadence-input label=${t('settings.billing.reduceBy')} type="number" .value=${String(this.editFee.break_reduction_percent)} @input=${(e: CustomEvent) => (this.editFee = { ...this.editFee, break_reduction_percent: e.detail })}></cadence-input>` : ''}
                  ` : ''}
                </div>
                <div class="actions">
                  <cadence-button @click=${() => this.saveEditFee()}>${t('common.actions.save')}</cadence-button>
                  <cadence-button variant="ghost" @click=${() => this.cancelEditFee()}>${t('common.actions.cancel')}</cadence-button>
                </div>
              </div>`
            : html`<div class="item">
                <div class="left"><strong>${f.name}</strong> <span class="muted">${f.scope === 'household' ? t('settings.billing.familyShort') : t('settings.billing.studentShort')} · ${t('settings.billing.feeTypes.' + f.type)} · ${money(f.base_amount, f.currency)}${f.due_day ? ` · ${t('settings.billing.dueDaySuffix', { day: f.due_day })}` : ''}${f.active ? '' : ` · ${t('settings.billing.archivedSuffix')}`}${f.payment_count ? ` · ${f.payment_count === 1 ? t('settings.billing.paymentsOne', { count: f.payment_count }) : t('settings.billing.paymentsMany', { count: f.payment_count })}` : ''}</span></div>
                <div class="actions">
                  ${f.payment_count
                    ? html`<span class="muted" title=${t('settings.billing.historyOnlyTitle')}>${t('settings.billing.historyOnly')}</span>`
                    : html`<button class="act" @click=${() => this.startEditFee(f)}>${t('settings.actions.edit')}</button>`}
                  <button class="act" @click=${() => this.archiveFee(f)}>${f.active ? t('settings.actions.archive') : t('settings.actions.restore')}</button>
                  ${f.payment_count ? '' : html`<button class="rm" @click=${() => this.deleteFee(f)}>${t('settings.actions.delete')}</button>`}
                </div>
              </div>`)}
          ${this.fees.length ? '' : html`<cadence-empty-state heading=${t('settings.billing.noFees')}></cadence-empty-state>`}
        </div>
      </cadence-card>

      <cadence-card heading=${t('settings.billing.priceTiersHeading')}>
        <div class="row" style="margin-bottom:12px">
          <cadence-input label=${t('settings.billing.tierName')} placeholder=${t('settings.billing.tierNamePlaceholder')} .value=${this.newTier.name} @input=${(e: CustomEvent) => (this.newTier = { ...this.newTier, name: e.detail })}></cadence-input>
          <cadence-select label=${t('settings.billing.adjustment')} .value=${this.newTier.adjustment_type}
            .options=${[{ value: 'percent', label: t('settings.billing.adjustmentTypes.percent') }, { value: 'fixed', label: t('settings.billing.adjustmentTypes.fixed') }]}
            @change=${(e: CustomEvent) => (this.newTier = { ...this.newTier, adjustment_type: e.detail })}></cadence-select>
          <cadence-input label=${t('settings.billing.value')} type="number" .value=${String(this.newTier.adjustment_value)} @input=${(e: CustomEvent) => (this.newTier = { ...this.newTier, adjustment_value: e.detail })}></cadence-input>
          <cadence-button variant="secondary" @click=${() => this.createTier()}>${t('settings.billing.addTier')}</cadence-button>
        </div>
        <div class="list">
          ${this.priceTiers.map((tier) => html`<div class="item"><div class="left"><strong>${tier.name}</strong> <span class="muted">${tier.adjustment_value ? (tier.adjustment_type === 'percent' ? `${tier.adjustment_value}%` : `${tier.adjustment_value}`) : t('settings.billing.noAdjustment')}${tier.is_default ? ` · ${t('settings.billing.default')}` : ''}</span></div>${tier.is_default ? '' : html`<button class="rm" @click=${() => this.delTier(tier.id)}>${t('settings.actions.delete')}</button>`}</div>`)}
        </div>
      </cadence-card>

      <cadence-card heading=${t('settings.billing.methodsHeading')}>
        <p class="muted" style="margin-top:0">${t('settings.billing.methodsHint')}</p>
        <div class="row" style="margin-bottom:12px">
          <cadence-input label=${t('settings.billing.addMethodLabel')} placeholder=${t('settings.billing.addMethodPlaceholder')} .value=${this.newMethod} @input=${(e: CustomEvent) => (this.newMethod = e.detail)}></cadence-input>
          <cadence-button variant="secondary" @click=${() => this.addMethod()}>${t('settings.billing.addMethod')}</cadence-button>
        </div>
        <div class="list">
          ${this.methods.map((m) => {
            const isStripe = m.provider === 'stripe';
            return html`<div class="item">
              <div class="left">
                <strong>${m.name}</strong>
                <span class="muted">${isStripe ? t('settings.billing.methodCard') : t('settings.billing.methodOffline')}${m.is_default ? ` · ${t('settings.billing.default')}` : ''}${(isStripe ? this.stripe?.enabled : m.enabled) ? '' : ` · ${t('settings.billing.disabledSuffix')}`}${m.payment_count ? ` · ${m.payment_count === 1 ? t('settings.billing.paymentsOne', { count: m.payment_count }) : t('settings.billing.paymentsMany', { count: m.payment_count })}` : ''}</span>
              </div>
              <div class="actions">
                ${isStripe
                  ? html`<span class="muted">${t('settings.billing.managedBelow')}</span>`
                  : html`
                    ${m.is_default ? html`<span class="muted">${t('settings.billing.default')}</span>` : html`<button class="act" @click=${() => this.setDefaultMethod(m.id)}>${t('settings.billing.makeDefault')}</button>`}
                    <button class="act" @click=${() => this.toggleMethod(m)}>${m.enabled ? t('settings.actions.disable') : t('settings.actions.enable')}</button>
                    ${m.payment_count ? '' : html`<button class="rm" @click=${() => this.deleteMethod(m.id)}>${t('settings.actions.delete')}</button>`}
                  `}
              </div>
            </div>`;
          })}
        </div>
      </cadence-card>

      ${this.stripe ? this.renderStripe() : ''}

      ${this.billing ? html`
      <cadence-card heading=${t('settings.billing.remindersHeading')}>
        <p class="muted" style="margin-top:0">${t('settings.billing.remindersHint')}</p>
        <div class="row" style="margin-bottom:14px">
          <cadence-toggle label=${t('settings.billing.sendReminders')} ?checked=${this.billing.reminders.enabled}
            @change=${(e: CustomEvent) => (this.billing = { ...this.billing, reminders: { ...this.billing.reminders, enabled: e.detail } })}></cadence-toggle>
          <cadence-input label=${t('settings.billing.daysBefore')} type="number" .value=${String(this.billing.reminders.days_before)}
            @input=${(e: CustomEvent) => (this.billing = { ...this.billing, reminders: { ...this.billing.reminders, days_before: e.detail } })}></cadence-input>
        </div>
        <div class="row">
          <cadence-toggle label=${t('settings.billing.applyLateFees')} ?checked=${this.billing.late_fee.enabled}
            @change=${(e: CustomEvent) => (this.billing = { ...this.billing, late_fee: { ...this.billing.late_fee, enabled: e.detail } })}></cadence-toggle>
          <cadence-input label=${t('settings.billing.lateFeeAmount')} type="number" .value=${String(this.billing.late_fee.amount)}
            @input=${(e: CustomEvent) => (this.billing = { ...this.billing, late_fee: { ...this.billing.late_fee, amount: e.detail } })}></cadence-input>
          <cadence-input label=${t('settings.billing.graceDays')} type="number" .value=${String(this.billing.late_fee.grace_days)}
            @input=${(e: CustomEvent) => (this.billing = { ...this.billing, late_fee: { ...this.billing.late_fee, grace_days: e.detail } })}></cadence-input>
        </div>
        <div style="margin-top:14px">
          <cadence-button @click=${() => this.saveBilling({
            reminders: { enabled: this.billing.reminders.enabled, days_before: Number(this.billing.reminders.days_before) || 0 },
            late_fee: { enabled: this.billing.late_fee.enabled, amount: Number(this.billing.late_fee.amount) || 0, grace_days: Number(this.billing.late_fee.grace_days) || 0 },
          })}>${t('settings.billing.saveAutomation')}</cadence-button>
        </div>
      </cadence-card>` : ''}
    </div>`;
  }

  private async loadContract() {
    try {
      const [current, versions] = await Promise.all([
        api.get<any>('/settings/contract'), api.get<any[]>('/settings/contract/versions'),
      ]);
      this.contract = current; this.contractVersions = versions;
      this.contractMd = current?.content_md ?? '';
    } catch { /* ignore */ }
  }

  private async importContractFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = ''; // allow re-picking the same file
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!['.md', '.markdown', '.mdown', '.txt'].some((ext) => name.endsWith(ext))) {
      showToast(t('settings.toast.importTypeError'), 'error');
      return;
    }
    if (file.size > 1_000_000) { showToast(t('settings.toast.fileTooLarge'), 'error'); return; }
    try {
      this.contractMd = await file.text();
      showToast(t('settings.toast.fileLoaded'), 'success');
    } catch { showToast(t('settings.toast.fileReadError'), 'error'); }
  }

  private async publishContract() {
    const content = this.contractMd.trim();
    if (!content) { showToast(t('settings.toast.contractEmpty'), 'error'); return; }
    this.publishing = true;
    try {
      await api.put('/settings/contract', { content_md: content });
      showToast(t('settings.toast.contractPublished'), 'success');
      await this.loadContract();
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.publishing = false; }
  }

  private renderContract() {
    const legacy = !!this.contract && !this.contract.content_md;
    return html`<div class="panel">
      <cadence-card heading=${t('settings.contract.heading')}>
        <div class="row" style="margin-bottom:8px;align-items:center">
          <cadence-button variant="ghost" @click=${() => this.contractFileInput?.click()}>${t('settings.contract.loadFile')}</cadence-button>
          <span class="muted">${t('settings.contract.writeHelp1')} <code>.md</code> / <code>.txt</code> ${t('settings.contract.writeHelp2')}</span>
        </div>
        <input id="contract-file" type="file" accept=".md,.markdown,.mdown,.txt,text/markdown,text/plain" hidden
          @change=${(e: Event) => this.importContractFile(e)} />
        ${legacy ? html`<p class="muted" style="color:var(--color-warning,var(--color-text-secondary))">${t('settings.contract.legacyNote')}</p>` : ''}
        <cadence-rich-text-editor .value=${this.contractMd}
          @input=${(e: CustomEvent) => (this.contractMd = e.detail)}></cadence-rich-text-editor>
        <div class="row" style="margin-top:12px;align-items:center">
          <cadence-button ?loading=${this.publishing} @click=${() => this.publishContract()}>${t('settings.contract.publish')}</cadence-button>
          ${this.contract ? html`<span class="muted">${t('settings.contract.currentVersion', { version: this.contract.version })}</span>` : html`<span class="muted">${t('settings.contract.noneYet')}</span>`}
        </div>
      </cadence-card>

      <cadence-card heading=${t('settings.contract.versionHistory')}>
        ${this.contractVersions.length
          ? html`<div class="versions">${this.contractVersions.map((v) => html`
              <div class="ver">
                <span class="v">${t('settings.contract.version', { version: v.version })}</span>
                <span class="muted ver-when">${new Date(v.created_at + 'Z').toLocaleString()}</span>
                <span class="muted ver-count">${t('settings.contract.accepted', { count: v.accepted_count ?? 0 })}</span>
                <cadence-button variant="ghost" @click=${() => (this.viewingVersion = v)}>${t('settings.contract.view')}</cadence-button>
              </div>`)}</div>`
          : html`<p class="muted">${t('settings.contract.noVersions')}</p>`}
      </cadence-card>
      ${this.renderVersionModal()}
    </div>`;
  }

  private renderVersionModal() {
    const v = this.viewingVersion;
    if (!v) return '';
    return html`
      <cadence-modal open heading=${t('settings.contract.modalHeading', { version: v.version })} @close=${() => (this.viewingVersion = null)}>
        <p class="muted" style="margin-top:0">
          ${(v.accepted_count ?? 0) === 1
            ? t('settings.contract.publishedByOne', { date: new Date(v.created_at + 'Z').toLocaleString(), count: v.accepted_count ?? 0 })
            : t('settings.contract.publishedByMany', { date: new Date(v.created_at + 'Z').toLocaleString(), count: v.accepted_count ?? 0 })}
        </p>
        <div class="ver-doc">${v.content_html ? unsafeHTML(v.content_html) : html`<p class="muted">${t('settings.contract.noContent')}</p>`}</div>
        <div slot="footer"><cadence-button @click=${() => (this.viewingVersion = null)}>${t('settings.actions.close')}</cadence-button></div>
      </cadence-modal>`;
  }

  private async createTag() {
    if (!this.newTag.name) return;
    try { await api.post('/tags', this.newTag); this.newTag = { name: '', color: '#2563eb' }; await this.load(); showToast(t('settings.toast.tagCreated'), 'success'); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async delTag(id: string) { await api.del(`/tags/${id}`); await this.load(); }

  private async createCat() { if (!this.newCat) return; await api.post('/dance/categories', { name: this.newCat, sort_order: this.categories.length + 1 }); this.newCat = ''; await this.load(); }
  private async delCat(id: string) { await api.del(`/dance/categories/${id}`); await this.load(); }
  private async createType(catId: string) {
    const name = this.newType[catId];
    if (!name) return;
    await api.post('/dance/types', { name, category_id: catId, sort_order: this.types.filter((ty) => ty.category_id === catId).length + 1 });
    this.newType = { ...this.newType, [catId]: '' };
    await this.load();
  }
  private async delType(id: string) { await api.del(`/dance/types/${id}`); await this.load(); }
  private async createLevel() { if (!this.newLevel) return; await api.post('/dance/levels', { name: this.newLevel, sort_order: this.levels.length + 1 }); this.newLevel = ''; await this.load(); }
  private async delLevel(id: string) { await api.del(`/dance/levels/${id}`); await this.load(); }

  private async createEventType() {
    const name = this.newEventType.trim();
    if (!name) return;
    try { await api.post('/events/types', { name }); this.newEventType = ''; await this.loadEventTypes(); showToast(t('settings.toast.eventTypeCreated'), 'success'); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async delEventType(id: string) {
    // The API blocks deletion of a type still used by events (409) — surface that as a toast.
    try { await api.del(`/events/types/${id}`); await this.loadEventTypes(); showToast(t('settings.toast.eventTypeDeleted'), 'success'); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }

  private renderTags() {
    return html`<div class="panel">
      <div class="row">
        <cadence-input label=${t('settings.tags.newTag')} placeholder=${t('settings.tags.namePlaceholder')} .value=${this.newTag.name} @input=${(e: CustomEvent) => (this.newTag = { ...this.newTag, name: e.detail })}></cadence-input>
        <input type="color" .value=${this.newTag.color} @input=${(e: Event) => (this.newTag = { ...this.newTag, color: (e.target as HTMLInputElement).value })} aria-label=${t('settings.tags.colorLabel')} />
        <cadence-button @click=${() => this.createTag()}>${t('settings.tags.addTag')}</cadence-button>
      </div>
      <div class="list">
        ${this.tags.map((tag) => html`<div class="item"><div class="left"><span class="dot" style="background:${tag.color}"></span>${tag.name}</div><button class="rm" @click=${() => this.delTag(tag.id)}>${t('settings.actions.delete')}</button></div>`)}
        ${this.tags.length ? '' : html`<cadence-empty-state heading=${t('settings.tags.empty')}></cadence-empty-state>`}
      </div>
    </div>`;
  }

  private renderDance() {
    return html`<div class="panel">
      <cadence-card heading=${t('settings.dance.categoriesTypes')}>
        <div class="row" style="margin-bottom:12px">
          <cadence-input label=${t('settings.dance.newCategory')} placeholder=${t('settings.dance.categoryPlaceholder')} .value=${this.newCat} @input=${(e: CustomEvent) => (this.newCat = e.detail)}></cadence-input>
          <cadence-button variant="secondary" @click=${() => this.createCat()}>${t('settings.dance.addCategory')}</cadence-button>
        </div>
        ${this.categories.map((cat) => html`
          <div class="cat"><div class="item"><span>${cat.name}</span><button class="rm" @click=${() => this.delCat(cat.id)}>${t('settings.actions.delete')}</button></div></div>
          <div class="nested list">
            ${this.types.filter((ty) => ty.category_id === cat.id).map((ty) => html`<div class="item"><span>${ty.name}</span><button class="rm" @click=${() => this.delType(ty.id)}>${t('settings.actions.delete')}</button></div>`)}
            <div class="row">
              <cadence-input placeholder=${t('settings.dance.typePlaceholder')} .value=${this.newType[cat.id] ?? ''} @input=${(e: CustomEvent) => (this.newType = { ...this.newType, [cat.id]: e.detail })}></cadence-input>
              <cadence-button variant="ghost" @click=${() => this.createType(cat.id)}>${t('settings.dance.addType')}</cadence-button>
            </div>
          </div>`)}
      </cadence-card>

      <cadence-card heading=${t('settings.dance.levelsHeading')}>
        <div class="row" style="margin-bottom:12px">
          <cadence-input placeholder=${t('settings.dance.levelPlaceholder')} .value=${this.newLevel} @input=${(e: CustomEvent) => (this.newLevel = e.detail)}></cadence-input>
          <cadence-button variant="secondary" @click=${() => this.createLevel()}>${t('settings.dance.addLevel')}</cadence-button>
        </div>
        <div class="list">
          ${this.levels.map((l) => html`<div class="item"><span>${l.name}</span><button class="rm" @click=${() => this.delLevel(l.id)}>${t('settings.actions.delete')}</button></div>`)}
        </div>
      </cadence-card>
    </div>`;
  }

  private renderEventTypes() {
    return html`<div class="panel">
      <p class="muted" style="margin:0">${t('settings.eventTypes.hint')}</p>
      <div class="row">
        <cadence-input label=${t('settings.eventTypes.newType')} placeholder=${t('settings.eventTypes.placeholder')} .value=${this.newEventType}
          @input=${(e: CustomEvent) => (this.newEventType = e.detail)} @enter=${() => this.createEventType()}></cadence-input>
        <cadence-button @click=${() => this.createEventType()}>${t('settings.eventTypes.addType')}</cadence-button>
      </div>
      <div class="list">
        ${this.eventTypes.map((et) => html`<div class="item"><span>${et.name}</span><button class="rm" @click=${() => this.delEventType(et.id)}>${t('settings.actions.delete')}</button></div>`)}
        ${this.eventTypes.length ? '' : html`<cadence-empty-state heading=${t('settings.eventTypes.empty')}></cadence-empty-state>`}
      </div>
    </div>`;
  }

  // ---- General: school profile + break months ----
  private renderGeneral() {
    const f = this.schoolForm;
    const set = (patch: any) => (this.schoolForm = { ...f, ...patch });
    return html`<div class="panel">
      <cadence-card heading=${t('settings.general.schoolIdentity')}>
        <div class="logo-row">
          <cadence-photo-upload endpoint="/settings/logo" name=${f.name || t('settings.general.schoolFallback')} .src=${this.school?.logo_url ?? ''} .size=${88}
            @photo-uploaded=${(e: CustomEvent) => this.onLogoUploaded(e.detail.url)}></cadence-photo-upload>
          <div class="logo-meta">
            <span class="field-label">${t('settings.general.logo')}</span>
            <span class="muted">${t('settings.general.logoHint')}</span>
            ${this.school?.logo_url ? html`<button class="act" style="align-self:flex-start" @click=${() => this.clearLogo()}>${t('settings.general.removeLogo')}</button>` : ''}
          </div>
        </div>
        <div class="form-grid" style="margin-top:18px">
          <cadence-input class="full" label=${t('settings.general.schoolName')} .value=${f.name} @input=${(e: CustomEvent) => set({ name: e.detail })}></cadence-input>
          <cadence-input class="full" label=${t('settings.general.tagline')} placeholder=${t('settings.general.taglinePlaceholder')} .value=${f.tagline} @input=${(e: CustomEvent) => set({ tagline: e.detail })}></cadence-input>
        </div>
      </cadence-card>

      <cadence-card heading=${t('settings.general.contactDetails')}>
        <div class="form-grid">
          <cadence-input label=${t('settings.general.contactEmail')} type="email" .value=${f.contact_email} @input=${(e: CustomEvent) => set({ contact_email: e.detail })}></cadence-input>
          <cadence-input label=${t('settings.general.phone')} .value=${f.phone} @input=${(e: CustomEvent) => set({ phone: e.detail })}></cadence-input>
          <cadence-input label=${t('settings.general.website')} .value=${f.website} @input=${(e: CustomEvent) => set({ website: e.detail })}></cadence-input>
          <cadence-input label=${t('settings.general.address')} .value=${f.address} @input=${(e: CustomEvent) => set({ address: e.detail })}></cadence-input>
        </div>
      </cadence-card>

      <cadence-card heading=${t('settings.general.regional')}>
        <div class="form-grid">
          <cadence-select label=${t('settings.general.timezone')} .value=${f.timezone}
            .options=${this.timezones.map((z) => ({ value: z, label: z }))}
            @change=${(e: CustomEvent) => set({ timezone: e.detail })}></cadence-select>
          <cadence-select label=${t('settings.general.defaultLanguage')} .value=${f.default_language}
            .options=${[{ value: 'en', label: 'English' }, { value: 'ro', label: 'Română' }]}
            @change=${(e: CustomEvent) => set({ default_language: e.detail })}></cadence-select>
        </div>
        <p class="muted" style="margin:14px 0 0">${t('settings.general.primaryCurrencyNote1')} <strong>${this.primaryCurrency || this.school?.default_currency || '—'}</strong> ${t('settings.general.primaryCurrencyNote2')} <strong>${t('settings.tabs.billing')}</strong> ${t('settings.general.primaryCurrencyNote3')}</p>
      </cadence-card>

      <cadence-card heading=${t('settings.general.breakMonths')}>
        <p class="muted" style="margin-top:0">${t('settings.general.breakMonthsHintPre')} <strong>${t('settings.tabs.billing')}</strong>${t('settings.general.breakMonthsHintPost')}</p>
        <div class="months">
          ${CadenceSettingsPage.MONTHS.map((label, i) => {
            const m = i + 1; const on = f.break_months.includes(m);
            return html`<button type="button" class="chip ${on ? 'on' : ''}" aria-pressed=${on ? 'true' : 'false'} @click=${() => this.toggleBreakMonth(m)}>${t(`settings.general.months.${label.toLowerCase()}`)}</button>`;
          })}
        </div>
      </cadence-card>

      <div class="save-row">
        <cadence-button ?disabled=${this.savingSchool} @click=${() => this.saveSchool()}>${this.savingSchool ? t('settings.general.saving') : t('settings.general.saveChanges')}</cadence-button>
      </div>
    </div>`;
  }

  // ---- Features: master feature-flag toggles ----
  private renderNotifications() {
    const items: { key: string; label: string; desc: string }[] = [
      { key: 'admin_new_student', label: t('settings.notifications.newStudent.label'), desc: t('settings.notifications.newStudent.desc') },
      { key: 'admin_payment_overdue', label: t('settings.notifications.paymentOverdue.label'), desc: t('settings.notifications.paymentOverdue.desc') },
      { key: 'admin_media_submitted', label: t('settings.notifications.mediaSubmitted.label'), desc: t('settings.notifications.mediaSubmitted.desc') },
      { key: 'admin_event_interest', label: t('settings.notifications.eventInterest.label'), desc: t('settings.notifications.eventInterest.desc') },
    ];
    return html`<div class="panel">
      <cadence-card heading=${t('settings.notifications.heading')}>
        <p class="muted" style="margin-top:0">${t('settings.notifications.hint')}</p>
        <div class="list">
          ${this.notifications == null
            ? html`<p class="muted">${t('common.states.loading')}</p>`
            : items.map((it) => html`
                <div class="item">
                  <div class="left"><div><strong>${it.label}</strong><div class="muted">${it.desc}</div></div></div>
                  <cadence-toggle ?checked=${this.notifications?.[it.key] !== false}
                    @change=${(e: CustomEvent) => this.toggleNotification(it.key, e.detail)}></cadence-toggle>
                </div>`)}
        </div>
      </cadence-card>
    </div>`;
  }

  private renderAppearance() {
    const canEdit = store.hasPermission('settings.edit');
    return html`<div class="panel">
      <cadence-card heading=${t('settings.appearance.heading')}>
        <p class="muted" style="margin-top:0">${t('settings.appearance.hint')}</p>
        ${this.themeCatalog.length === 0
          ? html`<p class="muted">${t('common.states.loading')}</p>`
          : html`<div class="theme-grid">
              ${this.themeCatalog.map((th) => html`
                <button
                  class="theme-card"
                  aria-pressed=${this.currentTheme === th.id ? 'true' : 'false'}
                  ?disabled=${!canEdit || this.savingTheme}
                  title=${th.name}
                  @click=${() => this.selectTheme(th.id)}
                >
                  <span class="theme-swatch" aria-hidden="true">
                    <span style="background:${th.swatch.sidebar}"></span>
                    <span style="background:${th.swatch.primary}"></span>
                    <span style="background:${th.swatch.bg}"></span>
                  </span>
                  <span class="theme-foot">
                    <span class="theme-name">${th.name}</span>
                    ${this.currentTheme === th.id ? html`<span class="theme-current">${t('settings.appearance.current')}</span>` : ''}
                  </span>
                </button>`)}
            </div>`}
        <p class="muted">${t('settings.appearance.darkModeHint')}</p>
      </cadence-card>
    </div>`;
  }

  private renderFeatures() {
    const flags: { key: string; label: string; desc: string }[] = [
      { key: 'feature_events', label: t('settings.features.flags.events.label'), desc: t('settings.features.flags.events.desc') },
      { key: 'feature_media', label: t('settings.features.flags.media.label'), desc: t('settings.features.flags.media.desc') },
      { key: 'feature_mailing', label: t('settings.features.flags.mailing.label'), desc: t('settings.features.flags.mailing.desc') },
      { key: 'feature_calendar', label: t('settings.features.flags.calendar.label'), desc: t('settings.features.flags.calendar.desc') },
      { key: 'feature_attendance', label: t('settings.features.flags.attendance.label'), desc: t('settings.features.flags.attendance.desc') },
      // Discipline modules (default-off) — labels keyed by flag name under the same path.
      ...moduleFeatureFlags().map((key) => ({
        key,
        label: t(`settings.features.flags.${key}.label`),
        desc: t(`settings.features.flags.${key}.desc`),
      })),
    ];
    return html`<div class="panel">
      <cadence-card heading=${t('settings.features.heading')}>
        <p class="muted" style="margin-top:0">${t('settings.features.hint')}</p>
        <div class="list">
          ${this.features == null
            ? html`<p class="muted">${t('common.states.loading')}</p>`
            : flags.map((fl) => html`
                <div class="item">
                  <div class="left"><div><strong>${fl.label}</strong><div class="muted">${fl.desc}</div></div></div>
                  <cadence-toggle ?checked=${this.features?.[fl.key] !== false}
                    @change=${(e: CustomEvent) => this.toggleFeature(fl.key, e.detail)}></cadence-toggle>
                </div>`)}
        </div>
      </cadence-card>
    </div>`;
  }

  private renderSection() {
    switch (this.section) {
      case 'general': return this.renderGeneral();
      case 'tags': return this.renderTags();
      case 'dance': return this.renderDance();
      case 'event-types': return this.renderEventTypes();
      case 'billing': return this.renderBilling();
      case 'email': return this.renderEmail();
      case 'media': return this.renderMedia();
      case 'calendar': return this.renderCalendar();
      case 'notifications': return this.renderNotifications();
      case 'appearance': return this.renderAppearance();
      case 'features': return this.renderFeatures();
      case 'contract': return this.renderContract();
      case 'backup':
        // System administration; guard direct URL access by the same permission the
        // sidebar uses to show the entry.
        return store.hasPermission('system.backup')
          ? html`<cadence-backup-settings></cadence-backup-settings>`
          : this.renderGeneral();
      default: return this.renderGeneral();
    }
  }

  private sectionTitle(): string {
    const found = SETTINGS_SECTIONS.find((s) => s.id === this.section);
    return found ? t(found.labelKey) : t('settings.title');
  }

  render() {
    // Section navigation lives in the sidebar (nested under Settings); this page just
    // renders the active section's content.
    return html`
      <h1>${t('settings.title')} · ${this.sectionTitle()}</h1>
      ${this.renderSection()}
    `;
  }
}
